import Swal from "sweetalert2";
import { get_token } from "@/router";

export function get_product_min_date() {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");

    return [
        `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
        `${pad(now.getHours())}:${pad(now.getMinutes())}`
    ].join("T");
}

export function format_product_attribute_text(value) {
    return String(value || "")
        .trim()
        .split(/[\s_-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
}

export function get_required_product_attribute_groups(grouped_attributes = []) {
    return grouped_attributes.filter((group) =>
        Array.isArray(group?.values) &&
        group.values.some((attribute) => has_product_attribute_option_id(attribute))
    );
}

export function has_product_attribute_option_id(attribute = {}) {
    return String(attribute?.id || "").trim() !== "";
}

export function is_valid_product_attribute_option(attribute = {}) {
    return has_product_attribute_option_id(attribute) &&
        attribute.value !== null &&
        attribute.value !== undefined &&
        String(attribute.value).trim() !== "";
}

export function auto_select_single_product_attribute_options(
    attribute_groups = [],
    selected_attributes = {},
    option_is_valid = has_product_attribute_option_id
) {
    const next_selected_attributes = { ...selected_attributes };

    attribute_groups.forEach((group) => {
        const valid_options = (group.values || []).filter((attribute) =>
            option_is_valid(attribute)
        );

        if (valid_options.length === 1 && !Reflect.get(next_selected_attributes, group.key)) {
            Reflect.set(next_selected_attributes, group.key, valid_options[0].id);
        }
    });

    return next_selected_attributes;
}

export function extract_selected_product_attribute_ids(attributes = []) {
    if (!Array.isArray(attributes) || !attributes.length) return [];

    return attributes
        .map((attribute) => {
            if (!attribute || typeof attribute !== "object") return attribute;
            return attribute.id;
        })
        .filter((attribute_id) => attribute_id !== null && attribute_id !== undefined && attribute_id !== "");
}

export function find_matching_product_cart_item(items = [], product_id, selected_attributes = []) {
    const product_id_string = String(product_id);
    const selected_attribute_ids = extract_selected_product_attribute_ids(selected_attributes)
        .map((attribute_id) => String(attribute_id))
        .sort();

    const matching_items = (items || []).filter((item) => {
        const item_product_id = item?.product?.id || item?.product_id;

        return String(item_product_id) === product_id_string;
    });

    return matching_items.find((item) => {
        const item_attribute_ids = extract_selected_product_attribute_ids(
            item?.attributes || item?.product_attributes || item?.attribute_values || []
        )
            .map((attribute_id) => String(attribute_id))
            .sort();

        return (
            item_attribute_ids.length === selected_attribute_ids.length &&
            item_attribute_ids.every((attribute_id, index) =>
                attribute_id === Reflect.get(selected_attribute_ids, index)
            )
        );
    }) || null;
}

export function get_product_rating_level(hover_rating = 0, new_rating = 0) {
    return ["", "Terrible", "Poor", "Average", "Good", "Excellent"][hover_rating || new_rating] || "Tap a star to rate";
}

function normalize_string(value) {
    return String(value || "").trim();
}

function normalize_email(value) {
    return normalize_string(value).toLowerCase();
}

function get_current_review_identity(user_details = {}) {
    return {
        user_id: normalize_string(user_details?.id),
        email: normalize_email(user_details?.email),
        base_profile_id: normalize_string(
            user_details?.base_profile?.id || user_details?.base_profile_id
        ),
    };
}

function get_review_identity(review = {}) {
    return {
        user_id: normalize_string(review?.base_profile?.user?.id || review?.user?.id),
        email: normalize_email(review?.base_profile?.user?.email || review?.user?.email),
        base_profile_id: normalize_string(review?.base_profile?.id),
    };
}

function matching_identity_values(current_value = "", review_value = "") {
    return Boolean(current_value && review_value && current_value === review_value);
}

export function review_belongs_to_current_user(review = {}, user_details = {}) {
    const current_identity = get_current_review_identity(user_details);
    const review_identity = get_review_identity(review);

    return Boolean(
        matching_identity_values(current_identity.user_id, review_identity.user_id) ||
        matching_identity_values(current_identity.email, review_identity.email) ||
        matching_identity_values(current_identity.base_profile_id, review_identity.base_profile_id)
    );
}

export function has_submitted_existing_product_review(product = {}, reviews = [], user_details = {}) {
    return Boolean(product?.user_has_rated_product) ||
        (reviews || []).some((review) => review_belongs_to_current_user(review, user_details));
}

export function open_edit_product_review_state(review = {}) {
    return {
        new_rating: Number(review?.rating || 0),
        product_review_text: String(review?.review || ""),
        hover_rating: 0,
        is_editing_product_review: true,
        editing_product_review_id: review?.id || null,
        show_product_reviews: true,
    };
}

export function cancel_edit_product_review_state() {
    return {
        new_rating: 0,
        hover_rating: 0,
        product_review_text: "",
        is_editing_product_review: false,
        editing_product_review_id: null,
    };
}

export function submit_product_review_request({
    product_id,
    rating,
    review,
    on_success,
}) {
    if (!product_id || !rating || !String(review || "").trim()) return;

    $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/create_product_review/`,
        type: "POST",
        headers: {
            Authorization: `Token ${get_token()}`,
            "Content-Type": "application/json",
        },
        data: JSON.stringify({
            product_id,
            rating,
            review,
        }),
        success: (response) => {
            if (response.status !== "success") return;
            if (typeof on_success === "function") {
                on_success(response);
            }
        }
    });
}

function update_product_review_rating_summary(product = {}, response = {}) {
    if (!product) return;

    product.user_has_rated_product = true;

    if (response.data?.average_rating) {
        product.average_rating = response.data.average_rating;
    }
}

function find_existing_product_review_index(product_reviews = [], editing_product_review_id = null, user_details = {}) {
    return product_reviews.findIndex((review) =>
        String(review?.id || "") === String(editing_product_review_id || "") ||
        review_belongs_to_current_user(review, user_details)
    );
}

function sync_submitted_product_review({
    product_reviews = [],
    submitted_review = null,
    editing_product_review_id = null,
    user_details = {},
}) {
    if (!submitted_review) return product_reviews;

    const existing_index = find_existing_product_review_index(
        product_reviews,
        editing_product_review_id,
        user_details
    );

    if (existing_index >= 0) {
        product_reviews.splice(existing_index, 1, submitted_review);
        return product_reviews;
    }

    product_reviews.unshift(submitted_review);
    return product_reviews;
}

function show_product_review_success_message(is_editing_product_review = false) {
    const title = is_editing_product_review ? "Review Updated" : "Review Submitted";
    const text = is_editing_product_review ? "Your review has been updated." : "Thank you for your feedback!";

    Swal.fire({
        icon: "success",
        title,
        text,
        showConfirmButton: false,
        timer: 1500,
        position: "bottom-end",
        toast: true
    });
}

export function handle_product_review_submission_success({
    response,
    product,
    product_reviews = [],
    editing_product_review_id = null,
    user_details = {},
    is_editing_product_review = false,
}) {
    update_product_review_rating_summary(product, response);
    sync_submitted_product_review({
        product_reviews,
        submitted_review: response.data?.product_rating || null,
        editing_product_review_id,
        user_details,
    });
    show_product_review_success_message(is_editing_product_review);

    return {
        product_reviews,
        new_rating: 0,
        hover_rating: 0,
        product_review_text: "",
        is_editing_product_review: false,
        editing_product_review_id: null,
    };
}
